import React, { useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import { romanToChordSymbol } from "../utils/romanToChordSymbol";
import { getExtensionColor, getScaleNotes } from "../utils/chordSuggestions";
import {
  chordToMidi,
  DEGREE_TO_SEMITONE_HARM_MINOR,
  DEGREE_TO_SEMITONE_MAJOR,
  parseChordToken,
} from "../utils/romanParser";
import { noteNameToMidi } from "../utils/chordUtils";

// Color mapping based on chord suggestion ranking
const SUGGESTION_COLORS = {
  green: "#23D18E", // Diatonic/in-scale
  yellow: "#FFDE58", // One non-scale tone
  red: "#F55353", // Multiple non-scale tones
};

const SHARP_ROOT_TO_FLAT: Record<string, string> = {
  "C#": "Db",
  "D#": "Eb",
  "F#": "Gb",
  "G#": "Ab",
  "A#": "Bb",
};

const formatChordRootWithFlats = (chordSymbol: string): string => {
  const rootMatch = chordSymbol.match(/^([A-G](?:#|b)?)(.*)$/);
  if (!rootMatch) return chordSymbol;

  const [, root, suffix] = rootMatch;
  const formattedRoot = SHARP_ROOT_TO_FLAT[root] ?? root;
  return `${formattedRoot}${suffix}`;
};

interface SegmentBox {
  number: number;
  startPercent: number; // 0-1 range
  endPercent: number; // 0-1 range (exclusive)
  color: string;
  numberBgColor: string; // Background color for the number
}

interface InteractiveChordProgressionProps {
  progression: {
    roman: string[];
    mode: "minor" | "major";
  };
  selectedKey: string;
  onProgressionChange: (
    ribbonPositions: Array<{ startPercent: number; endPercent: number }>
  ) => void;
  disabled?: boolean;
  onChordClick?: (chordIndex: number, roman: string) => void;
  onChordSwap?: (sourceIndex: number, targetIndex: number) => void;
  ribbonPositions?: Array<{ startPercent: number; endPercent: number }>; // Percentage-based positions
  currentChordIndex?: number; // Index of the currently playing chord (-1 or undefined = none)
  barCount?: number;
}

const InteractiveChordProgression: React.FC<
  InteractiveChordProgressionProps
> = ({
  progression,
  selectedKey,
  onProgressionChange,
  disabled = false,
  onChordClick,
  onChordSwap,
  ribbonPositions,
  currentChordIndex = -1,
  barCount = 1,
}) => {
  const safeBarCount = Math.max(1, barCount);
  const wrapDegree = useCallback((degree: number): number => {
    return ((((degree - 1) % 7) + 7) % 7) + 1;
  }, []);

  // Helper function to get chord color based on roman numeral
  const getChordColor = useCallback(
    (roman: string): string => {
      try {
        const token = parseChordToken(roman);
        const keyRootMidi = noteNameToMidi(selectedKey, 4);
        const isMinor = progression.mode === "minor";

        const chordNotes = chordToMidi(token, keyRootMidi, isMinor, true);
        const scaleNotes = getScaleNotes(
          selectedKey,
          isMinor ? "naturalMinor" : "major"
        );

        const scaleSemitones = isMinor
          ? DEGREE_TO_SEMITONE_HARM_MINOR
          : DEGREE_TO_SEMITONE_MAJOR;
        const rootDegree = wrapDegree(token.degree);
        const rootMidi =
          keyRootMidi + scaleSemitones[rootDegree - 1] + token.accidental;

        const extensionColor = getExtensionColor(
          chordNotes,
          rootMidi,
          scaleNotes
        );

        return SUGGESTION_COLORS[extensionColor];
      } catch {
        // If parsing fails, default to yellow
        return SUGGESTION_COLORS.yellow;
      }
    },
    [selectedKey, progression.mode, wrapDegree]
  );

  // Initialize segment boxes based on the progression or ribbonPositions prop
  // Default: equal distribution among chords
  const [segmentBoxes, setSegmentBoxes] = useState<SegmentBox[]>(() => {
    // If ribbonPositions are provided, use them; otherwise use equal distribution
    if (ribbonPositions && ribbonPositions.length > 0) {
      return ribbonPositions.map((pos, index) => {
        return {
          number: index + 1,
          startPercent: pos.startPercent,
          endPercent: pos.endPercent,
          color: getChordColor(progression.roman[index] || "I"),
          numberBgColor: "#34313B", // Dark grey/near-black text color
        };
      });
    }
    // Default: each chord gets equal share
    const chordCount = progression.roman.length;
    return progression.roman.map((_, index) => {
      return {
        number: index + 1,
        startPercent: index / chordCount,
        endPercent: (index + 1) / chordCount,
        color: getChordColor(progression.roman[index] || "I"),
        numberBgColor: "#34313B", // Dark grey/near-black text color
      };
    });
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<{
    boxIndex: number;
    edge: "left" | "right";
    startX: number;
    startBoxes: SegmentBox[];
  } | null>(null);
  const [draggedChordIndex, setDraggedChordIndex] = useState<number | null>(
    null
  );
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const didDragChordRef = useRef(false);

  // Ref to track the last positions we received from props (to prevent unnecessary updates)
  const lastReceivedPositionsRef = useRef<string | null>(null);

  // Update segment boxes when ribbonPositions prop changes (but not while dragging)
  useEffect(() => {
    // Don't update from props while actively dragging - prevents feedback loop
    if (isDragging) return;

    // Only update if ribbonPositions is explicitly provided (not undefined)
    if (ribbonPositions !== undefined) {
      const newPositionsStr = JSON.stringify(ribbonPositions);

      // Only update if the prop actually changed (prevents loop)
      if (newPositionsStr !== lastReceivedPositionsRef.current) {
        lastReceivedPositionsRef.current = newPositionsStr;

        if (ribbonPositions.length > 0) {
          // Use provided positions, colors will be set by the color effect
          const newSegmentBoxes = ribbonPositions.map((pos, index) => {
            return {
              number: index + 1,
              startPercent: pos.startPercent,
              endPercent: pos.endPercent,
              color: getChordColor(progression.roman[index] || "I"),
              numberBgColor: "#34313B",
            };
          });
          setSegmentBoxes(newSegmentBoxes);
        } else {
          // Empty array means reset to default (equal distribution)
          const chordCount = progression.roman.length;
          const newSegmentBoxes = progression.roman.map((_, index) => {
            return {
              number: index + 1,
              startPercent: index / chordCount,
              endPercent: (index + 1) / chordCount,
              color: getChordColor(progression.roman[index] || "I"),
              numberBgColor: "#34313B",
            };
          });
          setSegmentBoxes(newSegmentBoxes);
        }
      }
    }
    // If ribbonPositions is undefined, keep current segmentBoxes (user-controlled)
  }, [ribbonPositions, progression.roman, isDragging, getChordColor]);

  // Update colors when key, mode, or chords change (independent of positions)
  useEffect(() => {
    setSegmentBoxes((prev) =>
      prev.map((box, index) => {
        const roman = progression.roman[index] || "I";
        return {
          ...box,
          color: getChordColor(roman),
        };
      })
    );
  }, [getChordColor, progression.roman]);

  // Notify parent of ribbon position changes (only when not dragging to avoid feedback loop)
  useEffect(() => {
    // Don't notify parent while dragging - wait until drag ends
    if (isDragging) return;

    const positions = segmentBoxes.map((box) => ({
      startPercent: box.startPercent,
      endPercent: box.endPercent,
    }));
    onProgressionChange(positions);
  }, [segmentBoxes, onProgressionChange, isDragging]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, boxIndex: number, edge: "left" | "right") => {
      if (disabled) return;

      e.preventDefault();
      setIsDragging({
        boxIndex,
        edge,
        startX: e.clientX,
        startBoxes: [...segmentBoxes],
      });
    },
    [segmentBoxes, disabled]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;

      const deltaX = e.clientX - isDragging.startX;
      // Convert pixel delta to percentage delta
      const deltaPercent = deltaX / containerWidth;

      const newSegmentBoxes = [...isDragging.startBoxes];
      const { boxIndex, edge } = isDragging;

      // Minimum width for a segment (1% of total)
      const minWidth = 0.01;

      if (edge === "right") {
        // Dragging right edge
        const currentBox = newSegmentBoxes[boxIndex];
        const nextBox = newSegmentBoxes[boxIndex + 1];

        const newEndPercent = Math.max(
          currentBox.startPercent + minWidth, // Minimum width
          Math.min(
            nextBox ? nextBox.endPercent - minWidth : 1,
            currentBox.endPercent + deltaPercent
          )
        );

        newSegmentBoxes[boxIndex] = {
          ...currentBox,
          endPercent: newEndPercent,
        };

        if (nextBox) {
          newSegmentBoxes[boxIndex + 1] = {
            ...nextBox,
            startPercent: newEndPercent,
          };
        }
      } else {
        // Dragging left edge
        const currentBox = newSegmentBoxes[boxIndex];
        const prevBox = newSegmentBoxes[boxIndex - 1];

        const newStartPercent = Math.max(
          prevBox ? prevBox.startPercent + minWidth : 0,
          Math.min(
            currentBox.endPercent - minWidth, // Minimum width
            currentBox.startPercent + deltaPercent
          )
        );

        newSegmentBoxes[boxIndex] = {
          ...currentBox,
          startPercent: newStartPercent,
        };

        if (prevBox) {
          newSegmentBoxes[boxIndex - 1] = {
            ...prevBox,
            endPercent: newStartPercent,
          };
        }
      }

      setSegmentBoxes(newSegmentBoxes);
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  const handleChordDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, chordIndex: number) => {
      if (disabled || isDragging) {
        e.preventDefault();
        return;
      }

      didDragChordRef.current = true;
      setDraggedChordIndex(chordIndex);
      setDropTargetIndex(null);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", `${chordIndex}`);
    },
    [disabled, isDragging]
  );

  const handleChordDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, chordIndex: number) => {
      if (disabled || draggedChordIndex === null || draggedChordIndex === chordIndex) {
        return;
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dropTargetIndex !== chordIndex) {
        setDropTargetIndex(chordIndex);
      }
    },
    [disabled, draggedChordIndex, dropTargetIndex]
  );

  const handleChordDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
      if (disabled) {
        return;
      }

      e.preventDefault();
      if (draggedChordIndex === null || draggedChordIndex === targetIndex) {
        return;
      }

      onChordSwap?.(draggedChordIndex, targetIndex);
      setDraggedChordIndex(null);
      setDropTargetIndex(null);
    },
    [disabled, draggedChordIndex, onChordSwap]
  );

  const handleChordDragEnd = useCallback(() => {
    setDraggedChordIndex(null);
    setDropTargetIndex(null);
    window.setTimeout(() => {
      didDragChordRef.current = false;
    }, 0);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <InteractiveChordProgressionWrapper id="interactive-chord-progression">
      <BarMarkersRow id="chord-bar-markers">
        {Array.from({ length: safeBarCount }).map((_, index) => {
          const leftPercent = (index / safeBarCount) * 100;
          const label = index + 1;

          return (
            <BarMarker
              key={label}
              id={`bar-marker-${label}`}
              $left={`${leftPercent}%`}
            >
              <BarMarkerLine id={`bar-marker-line-${label}`} />
              <BarMarkerLabel id={`bar-marker-label-${label}`}>
                {label}
              </BarMarkerLabel>
            </BarMarker>
          );
        })}
      </BarMarkersRow>
      <ChordProgressionContainer
        id="chord-progression-container"
        ref={containerRef}
      >
        {segmentBoxes.map((box, index) => {
          // Visual width and position are now directly from percentages
          const widthPercent = (box.endPercent - box.startPercent) * 100;
          const leftPercent = box.startPercent * 100;
          const isCollapsed = widthPercent < 1;

          const displayWidth = isCollapsed ? 8 : widthPercent;

          const isActive = currentChordIndex === index;

          return (
            <InteractiveChordBox
              key={index}
              id={`chord-box-${index}`}
              $disabled={disabled}
              $isDropTarget={dropTargetIndex === index}
              $isDragSource={draggedChordIndex === index}
              $width={isCollapsed ? "8px" : `${displayWidth}%`}
              $left={`${leftPercent}%`}
              $minWidth={isCollapsed ? "8px" : "auto"}
              $zIndex={isCollapsed ? 10 : isActive ? 5 : 0}
              draggable={!disabled && !isDragging}
              onDragStart={(e) => handleChordDragStart(e, index)}
              onDragOver={(e) => handleChordDragOver(e, index)}
              onDrop={(e) => handleChordDrop(e, index)}
              onDragEnd={handleChordDragEnd}
            >
              {/* Visual ribbon with gap */}
              <RibbonVisual
                id={`ribbon-visual-${index}`}
                $backgroundColor={box.color}
                $isActive={isActive}
                $isFirst={index === 0}
                $isLast={index === segmentBoxes.length - 1}
              >
                <SegmentContent id={`segment-content-${index}`}>
                  {onChordClick && (
                    <ChordText
                      id={`chord-text-${index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (didDragChordRef.current) {
                          return;
                        }
                        if (!disabled) {
                          onChordClick(index, progression.roman[index]);
                        }
                      }}
                      $color={box.numberBgColor}
                      $disabled={disabled}
                    >
                      {(() => {
                        try {
                          const chordSymbol = romanToChordSymbol(
                            progression.roman[index],
                            selectedKey,
                            progression.mode
                          );
                          return formatChordRootWithFlats(chordSymbol);
                        } catch (error) {
                          // Fallback to Roman numeral if conversion fails
                          return progression.roman[index];
                        }
                      })()}
                    </ChordText>
                  )}
                </SegmentContent>
              </RibbonVisual>

              {/* Left resize handle */}
              {index > 0 && (
                <ResizeHandle
                  id={`resize-handle-left-${index}`}
                  $position="left"
                  onMouseDown={(e) => handleMouseDown(e, index, "left")}
                />
              )}

              {/* Right resize handle */}
              {index < segmentBoxes.length - 1 && (
                <ResizeHandle
                  id={`resize-handle-right-${index}`}
                  $position="right"
                  onMouseDown={(e) => handleMouseDown(e, index, "right")}
                />
              )}
            </InteractiveChordBox>
          );
        })}
      </ChordProgressionContainer>
    </InteractiveChordProgressionWrapper>
  );
};

export default InteractiveChordProgression;

// Styled Components
const InteractiveChordProgressionWrapper = styled.div`
  border-radius: 8px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const ChordProgressionContainer = styled.div`
  position: relative;
  height: 40px;
  border-radius: 6px;
  margin-bottom: 10px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 70px;
  }
`;

const InteractiveChordBox = styled.div<{
  $disabled: boolean;
  $isDropTarget: boolean;
  $isDragSource: boolean;
  $width: string;
  $left: string;
  $minWidth: string;
  $zIndex: number;
}>`
  position: absolute;
  top: 0;
  height: 100%;
  color: #333;
  font-weight: 600;
  border: none;
  cursor: move;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => props.$width};
  left: ${(props) => props.$left};
  min-width: ${(props) => props.$minWidth};
  z-index: ${(props) => props.$zIndex};
  opacity: ${(props) => (props.$isDragSource ? 0.55 : 1)};

  ${(props) =>
    props.$isDropTarget &&
    `
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.85);
  `}

  ${(props) =>
    props.$disabled &&
    `
    cursor: not-allowed;
    opacity: 0.6;
  `}
`;

const RibbonVisual = styled.div<{
  $backgroundColor: string;
  $isActive: boolean;
  $isFirst: boolean;
  $isLast: boolean;
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => (props.$isFirst ? "0" : "2px")};
  right: ${(props) => (props.$isLast ? "0" : "2px")};
  background-color: ${(props) => props.$backgroundColor};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.1s ease;
  pointer-events: none; /* Let events pass through to parent and handles */

  /* Active state - glowing effect similar to sequencer steps */
  ${(props) =>
    props.$isActive &&
    `
    box-shadow: 
      0 0 10px ${props.$backgroundColor},
      0 0 20px ${props.$backgroundColor},
      inset 0 0 10px rgba(255, 255, 255, 0.3);
    filter: brightness(1.2);
    transform: scaleY(1.05);
  `}
`;

const SegmentContent = styled.div`
  text-align: center;
  pointer-events: none;
  z-index: 1;
  min-width: 20px;
`;

const ChordText = styled.div<{ $color: string; $disabled: boolean }>`
  pointer-events: auto;
  user-select: none;
  color: ${(props) => props.$color};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-family: "Dogica Pixel", monospace;
  font-size: 14px;
  font-weight: 400;
  margin-top: 2px;
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
`;

const ResizeHandle = styled.div<{ $position: "left" | "right" }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8px;
  background-color: rgba(255, 255, 255, 0.3);
  cursor: ew-resize;
  opacity: 0;
  transition: opacity 0.2s ease, background-color 0.2s ease;
  z-index: 2;

  ${(props) =>
    props.$position === "left"
      ? `
    left: 0;
    border-radius: 0 4px 4px 0;
  `
      : `
    right: 0;
    border-radius: 4px 0 0 4px;
  `}

  &:hover {
    background-color: rgba(255, 255, 255, 0.6);
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.8);
  }

  ${InteractiveChordBox}:hover & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    width: 12px;
  }
`;

const BarMarkersRow = styled.div`
  position: relative;
  height: 20px;
  margin-bottom: 6px;
  pointer-events: none;
`;

const BarMarker = styled.div<{ $left: string }>`
  position: absolute;
  top: 50%;
  left: ${(props) => props.$left};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BarMarkerLabel = styled.div`
  font-family: "Dogica Pixel", monospace;
  font-size: 10px;
  color: #f2f2f2;
`;

const BarMarkerLine = styled.div`
  width: 3px;
  height: 12px;
  background-color: #f2f2f2;
  border-radius: 1px;
`;
